export const MIN_ZOOM_FRACTION = 1 / 1000;
export const RESET_ZOOM_THRESHOLD = 0.999;
export const DEFAULT_ZOOM_STEP = 1.5;

export type DomainBounds = {
    readonly min: number;
    readonly max: number;
};

export type BaseDataPoint = {
    readonly xValue: number;
};

export const calculateBounds = <T extends BaseDataPoint>(
    data: ReadonlyArray<T>
): DomainBounds => {
    if (data.length === 0) {
        return { min: 0, max: 0 };
    }
    const values = data.map((d) => d.xValue);
    return {
        min: Math.min(...values),
        max: Math.max(...values)
    };
};

export const calculateZoomDomain = (
    currentDomain: [number, number] | null,
    dataBounds: DomainBounds,
    zoomFactor: number,
    centerPoint?: number
): [number, number] | null => {
    const fullRange = dataBounds.max - dataBounds.min;
    if (fullRange <= 0) {
        return currentDomain;
    }

    const domain = currentDomain ?? [dataBounds.min, dataBounds.max];
    const currentRange = domain[1] - domain[0];

    const minRange = fullRange * MIN_ZOOM_FRACTION;
    const targetRange = Math.min(
        fullRange,
        Math.max(minRange, currentRange * zoomFactor)
    );

    // Determine zoom center
    const center = centerPoint ?? (domain[0] + domain[1]) / 2;
    const centerFrac =
        currentRange > 0 ? (center - domain[0]) / currentRange : 0.5;

    let newMin = center - centerFrac * targetRange;
    let newMax = newMin + targetRange;

    // Clamp while preserving span by shifting when we hit edges
    if (newMin < dataBounds.min) {
        newMax += dataBounds.min - newMin;
        newMin = dataBounds.min;
    }
    if (newMax > dataBounds.max) {
        newMin -= newMax - dataBounds.max;
        newMax = dataBounds.max;
    }

    newMin = Math.max(dataBounds.min, newMin);
    newMax = Math.min(dataBounds.max, newMax);

    // If we're effectively at the full extent, return null to clear zoom state
    if (newMax - newMin >= fullRange * RESET_ZOOM_THRESHOLD) {
        return null;
    }

    return [newMin, newMax];
};

export const isZoomed = (
    currentDomain: [number, number] | null,
    dataBounds: DomainBounds
): boolean => {
    if (!currentDomain) {
        return false;
    }
    const fullRange = dataBounds.max - dataBounds.min;
    const currentRange = currentDomain[1] - currentDomain[0];
    return currentRange < fullRange * RESET_ZOOM_THRESHOLD;
};

export const canZoomIn = (
    currentDomain: [number, number] | null,
    dataBounds: DomainBounds
): boolean => {
    const domain = currentDomain ?? [dataBounds.min, dataBounds.max];
    const currentRange = domain[1] - domain[0];
    const fullRange = dataBounds.max - dataBounds.min;
    return currentRange > fullRange * MIN_ZOOM_FRACTION;
};

export const canZoomOut = (
    currentDomain: [number, number] | null,
    dataBounds: DomainBounds
): boolean => {
    return isZoomed(currentDomain, dataBounds);
};

export const calculateAxisTicks = (
    domain: [number, number],
    tickInterval: number
): number[] => {
    const ticks: number[] = [];
    const firstTick = Math.ceil(domain[0] / tickInterval) * tickInterval;

    for (let i = firstTick; i <= domain[1]; i += tickInterval) {
        ticks.push(i);
    }

    // Add domain start if not covered
    if (ticks.length === 0 || domain[0] < ticks[0]) {
        ticks.unshift(Math.round(domain[0]));
    }

    // Add domain end if not covered
    const lastTick = ticks[ticks.length - 1];
    if (domain[1] > lastTick) {
        ticks.push(Math.round(domain[1]));
    }

    return ticks;
};

export const calculateDragDomain = (
    dragStartDomain: [number, number],
    dragStartX: number,
    currentX: number,
    containerWidth: number,
    dataBounds: DomainBounds
): [number, number] => {
    // Inverted: drag right = move view left
    const pixelDelta = dragStartX - currentX;

    // Calculate domain range per pixel
    const domainRange = dragStartDomain[1] - dragStartDomain[0];
    const domainPerPixel = domainRange / containerWidth;
    const domainDelta = pixelDelta * domainPerPixel;

    let newMin = dragStartDomain[0] + domainDelta;
    let newMax = dragStartDomain[1] + domainDelta;
    const newRange = newMax - newMin;

    // Clamp to bounds while preserving range
    if (newMin < dataBounds.min) {
        newMin = dataBounds.min;
        newMax = newMin + newRange;
    }
    if (newMax > dataBounds.max) {
        newMax = dataBounds.max;
        newMin = newMax - newRange;
    }

    // Final clamp to ensure we don't exceed bounds
    newMin = Math.max(dataBounds.min, newMin);
    newMax = Math.min(dataBounds.max, newMax);

    return [newMin, newMax];
};
